import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { GeoObjectMeshOptions, GeoObjectTypes, PreProcessingTypes, RawDataGroup } from '@visualizer/types';
import { getOnlyGeometryFromRawData } from './utils';
import { EditableGeoObject } from './EditableGeoObject';
import { getGlobalState } from '@gui/store/persistent/persistentStore';

interface Object3DGroup {
    meshes: THREE.BatchedMesh;
    edges: THREE.LineSegments;
}

type Objects3D<T extends GeoObjectTypes> = {
    [K in T[number]]: Object3DGroup;
};

export abstract class GeoObjectBatched<T extends GeoObjectTypes> extends EditableGeoObject<T> {
    protected objects: Objects3D<T>;
    private isDisposed = false;

    // Reconstruction of the edges after the save button
    protected readonly sourceGeoms: Partial<Record<T[number], THREE.BufferGeometry[]>> = {};
    private readonly thresholdAngleUsed: number;

    constructor({ name, colors, data, mode, types, thresholdAngle }: GeoObjectMeshOptions<T>) {
        super({ name, colors, mode, types });
        this.thresholdAngleUsed = thresholdAngle ?? 10;

        const meshes = this.initMeshes(data, thresholdAngle);
        this.objects = meshes.batched;

        Object.values<Object3DGroup>(this.objects).forEach((e) => {
            e.edges.renderOrder = 2;
            this.add(e.meshes);
            this.add(e.edges);
        });
    }

    override export(): { name: string; mesh: THREE.Mesh }[] {
        const toReturn: { name: string; mesh: THREE.Mesh }[] = [];
        for (const key of Object.keys(this.objects) as Array<T[number]>) {
            toReturn.push({ name: key, mesh: this.objects[key].meshes.clone() });
        }
        return toReturn;
    }

    override dispose(): void {
        if (this.isDisposed) {
            return;
        }
        this.isDisposed = true;

        super.dispose();

        Object.values<Object3DGroup>(this.objects).forEach((e) => {
            e.meshes.dispose();
            e.edges.geometry.dispose();
        });
    }
    protected override onStandardMode(): void {
        const showStrokes = getGlobalState().ambient.showStrokes;
        this.setMaterialMode(false, false);
        Object.values<Object3DGroup>(this.objects).forEach((e) => {
            e.meshes.visible = true;
            e.edges.visible = showStrokes;
        });
    }

    protected override onRenderMode(): void {
        const showStrokes = getGlobalState().ambient.showStrokes;
        this.setMaterialMode(true, true);
        this.types.forEach((type) => {
            const group = this.objects[type];
            group.meshes.visible = true;
            group.edges.visible = showStrokes && this.shouldShowEdgesInRenderMode(type);
        });
    }

    protected shouldShowEdgesInRenderMode(_type: T[number]): boolean {
        return false;
    }

    private setMaterialMode(useShadedMaterial: boolean, enableShadows: boolean): void {
        this.types.forEach((e) => {
            const o = this.objects[e];
            o.meshes.material = useShadedMaterial ? this.materials[e].shadowMaterial : this.materials[e].material;
            o.meshes.castShadow = enableShadows && this.shouldCastShadowsForType(e);
            o.meshes.receiveShadow = enableShadows;
        });
    }

    protected shouldCastShadowsForType(_type: T[number]): boolean {
        return true;
    }

    override hasIndividualMeshes(): boolean {
        return true;
    }

    private initMeshes(data: RawDataGroup[], thresholdAngle: number = 10): { batched: Objects3D<T> } {
        const objects: Partial<Objects3D<T>> = {};

        for (const key of this.types) {
            const group = data.find((d) => d.type === key);

            const groupGeoms: THREE.BufferGeometry[] = [];
            if (group) {
                for (const d of group.objects) {
                    groupGeoms.push(this.modifyGeometry(group.type, getOnlyGeometryFromRawData(d)));
                }
            }

            // always create meshes/edges so later "add to batch" works even if starting empty
            objects[key] = {
                meshes: this.createBatchedMesh(groupGeoms, this.materials[key].material),
                edges: groupGeoms.length
                    ? this.createEdges(groupGeoms, thresholdAngle, this.materials[key].stroke)
                    : new THREE.LineSegments(new THREE.BufferGeometry(), this.materials[key].stroke),
            };

            this.sourceGeoms[key] = groupGeoms;
        }
        return { batched: objects as Objects3D<T> };
    }

    protected modifyGeometry(_type: PreProcessingTypes, geom: THREE.BufferGeometry): THREE.BufferGeometry {
        return geom;
    }

    private createEdges(geoms: THREE.BufferGeometry[], thresholdAngle: number, material: THREE.Material): THREE.LineSegments {
        const edgesArray: THREE.BufferGeometry[] = [];

        geoms.forEach((g) => {
            const eg = new THREE.EdgesGeometry(g, thresholdAngle);
            edgesArray.push(eg);
        });

        const merged = mergeGeometries(edgesArray, false)!;
        edgesArray.forEach((e) => e.dispose());

        return new THREE.LineSegments(merged, material);
    }

    private createBatchedMesh(data: THREE.BufferGeometry[], material: THREE.Material): THREE.BatchedMesh {
        const baseVertices = data.reduce((sum, g) => sum + (g.attributes.position?.count ?? 0), 0);
        const baseIndices = data.reduce((sum, g) => sum + (g.index?.count ?? 0), 0);

        // headroom so we can add later (especially important for CUSTOM which may start empty)
        const EXTRA_GEOMS = 256;
        const EXTRA_VERTICES = 200_000;
        const EXTRA_INDICES = 400_000;

        const maxGeometryCount = Math.max(data.length + EXTRA_GEOMS, 1);
        const maxVertexCount = Math.max(baseVertices + EXTRA_VERTICES, 1);
        const maxIndexCount = Math.max(baseIndices + EXTRA_INDICES, 1);

        const batchedMesh = new THREE.BatchedMesh(maxGeometryCount, maxVertexCount, maxIndexCount, material);

        for (const geometry of data) {
            geometry.computeVertexNormals();
            const geomId = batchedMesh.addGeometry(geometry);
            const instanceId = batchedMesh.addInstance(geomId);
            batchedMesh.setMatrixAt(instanceId, new THREE.Matrix4());
        }

        batchedMesh.computeBoundingBox();
        batchedMesh.computeBoundingSphere();
        return batchedMesh;
    }

    // Method used to rebuild merged edges after the save button of editGeodataMode has been clicked
    public rebuildMergedEdges(): void {
        console.warn('[rebuildMergedEdges]', this.name);

        const tmp = new THREE.Matrix4();
        const pos = new THREE.Vector3();
        const quat = new THREE.Quaternion();
        const scale = new THREE.Vector3();

        // Dispose old geometries
        for (const key of this.types) {
            const grp = this.objects[key];
            grp.edges.geometry.dispose();
        }
        for (const key of this.types) {
            const geoms = this.sourceGeoms[key];
            const grp = this.objects[key];
            if (!geoms || geoms.length === 0) continue;

            // Figure out which instances are "alive" (scale != 0)
            const aliveGeoms: THREE.BufferGeometry[] = [];
            for (let instanceId = 0; instanceId < geoms.length; instanceId++) {
                grp.meshes.getMatrixAt(instanceId, tmp);
                tmp.decompose(pos, quat, scale);

                const eps = 1e-6;
                const dead = Math.abs(scale.x) < eps && Math.abs(scale.y) < eps && Math.abs(scale.z) < eps;

                if (!dead) aliveGeoms.push(geoms[instanceId]);
            }

            if (aliveGeoms.length === 0) {
                // nothing left of this type
                grp.edges.geometry = new THREE.BufferGeometry();
                continue;
            }

            // Rebuild merged edges for this type
            const edgesArray = aliveGeoms.map((g) => new THREE.EdgesGeometry(g, this.thresholdAngleUsed));
            const mergedEdges = mergeGeometries(edgesArray, false)!;
            edgesArray.forEach((e) => e.dispose());

            grp.edges.geometry = mergedEdges;
        }
    }
}
